import { normalizeApiPath } from "./api-path";

/**
 * Client-side session renewal.
 *
 * The access token lives 15 minutes, so any long-lived page will meet a
 * 401 eventually. This wrapper turns that into an invisible retry.
 */

/**
 * Collapse concurrent calls into one in-flight invocation.
 *
 * Correctness, not performance: a page that fires six requests at once
 * would otherwise fire six refreshes, presenting the same rotated token
 * six times. Five of those read as replay attacks server-side and revoke
 * the whole family — logging the user out because their page was busy.
 *
 * Rejections are not cached; a failed refresh must be retryable.
 */
export function singleFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (!inFlight) {
      inFlight = fn().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
}

/**
 * Endpoints that hand out a session rather than consume one.
 *
 * Their 401 means "wrong credentials" or "you have no session" — a state a
 * new access token cannot change. Renewing and replaying them is pure waste,
 * and on `/auth/login` it is worse than waste: one wrong password used to put
 * two POSTs on the wire, because the 401 was read as an expiry.
 *
 * Matched on the path so a route that merely contains the word (say
 * `/admin/login-attempts`) does not inherit the opt-out, and so both URL
 * shapes resolve alike — the same-origin `/api/auth/login` used in dev and
 * the dedicated-host `https://api.<domain>/auth/login` used in staging/prod.
 */
const SESSION_ENDPOINTS = new Set([
  "/auth/login",
  "/auth/signup",
  "/auth/refresh",
  "/auth/reset-password",
]);

export function establishesSession(url: string): boolean {
  // `new URL` needs an absolute input; the base is only a parsing scaffold
  // and never reaches the network.
  let path: string;
  try {
    path = new URL(url, "https://placeholder.invalid").pathname;
  } catch {
    return false;
  }
  return SESSION_ENDPOINTS.has(normalizeApiPath(path));
}

/**
 * Response header by which the API tells the client whether renewing is even
 * possible: "1" when a refresh cookie arrived, "0" when none did.
 *
 * The client cannot work this out on its own — both auth cookies are HttpOnly
 * by design, and every localStorage-based substitute gets it wrong in a way
 * that signs people out: Safari's ITP evicts script-writable storage after
 * ~7 days of no interaction while leaving server-set cookies alone, and
 * localStorage is per-origin while the auth cookie is `Domain=.<apex>` so it
 * deliberately spans `admin.*`. The server is the only party that knows.
 *
 * Absence means "renew" — never "skip". A response without the header must
 * behave the way it did before the header existed; guessing the cheap way on
 * a real session is the one failure mode worth designing against.
 */
export const SESSION_RENEWABLE_HEADER = "X-Session-Renewable";

export interface RefreshOptions {
  /**
   * Whether a 401 from this request is worth renewing for. False for the
   * session-establishing endpoints above. Defaults to true, so a caller that
   * does not care keeps the old behaviour.
   */
  renewable?: boolean;
}

/**
 * Run a request; on 401, refresh once and retry exactly once.
 *
 * Only 401 triggers this. A 403 is the server answering "you may not",
 * which a new token will not change — retrying it would hammer the API
 * on every permission boundary.
 */
export async function withRefresh(
  doFetch: () => Promise<Response>,
  refresh: () => Promise<boolean>,
  { renewable = true }: RefreshOptions = {}
): Promise<Response> {
  const first = await doFetch();
  if (first.status !== 401 || !renewable) return first;

  // The server already told us there is no session behind this 401 — an
  // anonymous visitor, whose renewal would be a guaranteed-401 round trip.
  if (first.headers.get(SESSION_RENEWABLE_HEADER) === "0") return first;

  const renewed = await refresh();
  if (!renewed) return first;

  return doFetch();
}
